import React from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  Alert,
  Card,
  CardContent,
  Rating,
  Avatar,
} from "@mui/material";
import {
  Feedback as FeedbackIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  ChatSessionDto,
  SessionFeedback,
} from "@/apis/types/SuperChatLogTypes";

interface FeedbackTabProps {
  session: ChatSessionDto;
  feedback: SessionFeedback | null;
  loading?: boolean;
}

/**
 * 피드백 탭 컴포넌트
 *
 * @description 세션에 등록된 피드백 정보를 표시하는 탭
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export function FeedbackTab({ session, feedback, loading }: FeedbackTabProps) {
  // 로딩 중인 경우
  if (loading) {
    return (
      <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
        <Typography variant="body1" color="text.secondary">
          피드백 정보를 불러오는 중...
        </Typography>
      </Box>
    );
  }

  // 피드백이 없는 경우
  if (!feedback) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert
          severity="info"
          icon={<FeedbackIcon />}
          sx={{
            borderRadius: 2,
            backgroundColor: "info.50",
            border: "1px solid",
            borderColor: "info.200",
          }}
        >
          <Typography variant="body1" fontWeight="500">
            등록된 피드백이 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            이 세션에 대한 피드백이 아직 등록되지 않았습니다. 우측 상단의
            "Feedback" 버튼을 클릭하여 피드백을 등록할 수 있습니다.
          </Typography>
        </Alert>
      </Box>
    );
  }

  // feedback prop을 사용

  return (
    <Box sx={{ p: 4 }}>
      <Stack spacing={3}>
        {/* 피드백 헤더 */}
        <Box>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: "primary.main",
                width: 40,
                height: 40,
              }}
            >
              <FeedbackIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                세션 피드백
              </Typography>
              <Typography variant="body2" color="text.secondary">
                관리자가 등록한 세션 평가 및 피드백입니다
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* 피드백 카드 */}
        <Card
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 3,
            backgroundColor: "background.paper",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* 평점 */}
              {feedback.rating && (
                <Box>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" fontWeight="600">
                      평점
                    </Typography>
                    <Rating
                      value={feedback.rating}
                      readOnly
                      size="medium"
                      icon={<StarIcon fontSize="inherit" />}
                      emptyIcon={<StarIcon fontSize="inherit" />}
                    />
                    <Typography variant="body1" color="text.secondary">
                      ({feedback.rating}/5)
                    </Typography>
                  </Stack>
                </Box>
              )}

              {/* 카테고리 */}
              {feedback.category && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="600"
                    sx={{ mb: 1 }}
                  >
                    카테고리
                  </Typography>
                  <Chip
                    label={feedback.category}
                    color="primary"
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  />
                </Box>
              )}

              <Divider />

              {/* 피드백 내용 */}
              <Box>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                  피드백 내용
                </Typography>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: "grey.50",
                    border: "1px solid",
                    borderColor: "grey.200",
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {feedback.content || "피드백 내용이 없습니다."}
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              {/* 피드백 메타 정보 */}
              <Box>
                <Stack direction="row" spacing={4} flexWrap="wrap">
                  {/* 작성자 */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      작성자:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {feedback.createdBy || "관리자"}
                    </Typography>
                  </Stack>

                  {/* 작성일 */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <ScheduleIcon color="action" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      작성일:
                    </Typography>
                    <Typography variant="body2" fontWeight="500">
                      {feedback.createdAt
                        ? new Date(feedback.createdAt).toLocaleString("ko-KR", {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "-"}
                    </Typography>
                  </Stack>
                </Stack>

                {/* 수정일 (있는 경우) */}
                {feedback.updatedAt &&
                  feedback.updatedAt !== feedback.createdAt && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mt: 1 }}
                    >
                      <ScheduleIcon color="action" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        수정일:
                      </Typography>
                      <Typography variant="body2" fontWeight="500">
                        {new Date(feedback.updatedAt).toLocaleString("ko-KR", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                    </Stack>
                  )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
